import {
  createBaseThemeOptions,
  createUnifiedTheme,
  genPageTheme,
  palettes,
  shapes,
} from '@backstage/theme';

const tokens = {
  colors: {
    // Cores primárias
    primary: '#000050',
    secondary: '#c4c9e6',
    link: '#3b82f6',
    error: '#cc2532',
    white: '#ffffff',

    // Escala de cinzas semântica
    gray: {
      lightest: '#fcfdfe', // sidebar light
      lighter: '#f7fafc', // background light
      light: '#eef1f7', // hover light
      medium: '#e2e8f0', // divider light
      neutral: '#9397bf', // navigator dark
      dark: '#374151', // text/divider dark
      darker: '#1f2937', // card dark
      darkest: '#111827', // background dark
    },

    // Cores específicas que não seguem a escala
    special: {
      sidebarDark: '#1d2735',
      textSecondary: '#6b7280',
      textLight: '#d1d5db',
    },
  },
} as const;

const themeColors = {
  light: {
    primary: tokens.colors.primary,
    contrastText: tokens.colors.white,
    primaryDark: '#000040',
    primaryLight: '#000060',
    secondary: tokens.colors.primary,
    secondaryContrastText: tokens.colors.white,
    secondaryDark: '#2563eb',
    secondaryLight: '#232766',
    error: tokens.colors.error,
    errorContrastText: tokens.colors.white,
    errorDark: '#b91c1c',
    errorLight: '#ef4444',
    background: tokens.colors.gray.lighter,
    paper: tokens.colors.white,
    link: tokens.colors.link,
    navigationBackground: tokens.colors.gray.lightest,
    navigationIndicator: tokens.colors.primary,
    navigationColor: tokens.colors.gray.dark,
    navigationSelectedColor: tokens.colors.primary,
    navigationHoverBackground: '#f3f4f6',
    navigationSubmenuBackground: tokens.colors.white,

    card: tokens.colors.white,
    header: tokens.colors.white,
    divider: tokens.colors.gray.medium,
    text: tokens.colors.gray.darkest,
  },
  dark: {
    primary: tokens.colors.white,
    contrastText: tokens.colors.primary,
    primaryDark: '#e2e2ff',
    primaryLight: tokens.colors.white,
    secondary: tokens.colors.secondary,
    secondaryContrastText: tokens.colors.primary,
    secondaryDark: '#e2e2ff',
    secondaryLight: tokens.colors.white,
    error: tokens.colors.error,
    errorContrastText: tokens.colors.white,
    errorDark: '#b91c1c',
    errorLight: '#ef4444',
    background: tokens.colors.gray.darkest,
    paper: tokens.colors.gray.darker,
    link: tokens.colors.link,
    navigationBackground: tokens.colors.special.sidebarDark,
    navigationIndicator: tokens.colors.gray.neutral,
    navigationColor: tokens.colors.special.textLight,
    navigationSelectedColor: tokens.colors.white,
    navigationHoverBackground: tokens.colors.gray.dark,
    navigationSubmenuBackground: tokens.colors.gray.darker,

    card: tokens.colors.gray.darker,
    header: tokens.colors.gray.darker,
    divider: tokens.colors.gray.dark,
    text: tokens.colors.white,
  },
};

function createPalette(mode: 'light' | 'dark') {
  const config = themeColors[mode];
  const basePalette = palettes[mode];

  return createBaseThemeOptions({
    palette: {
      ...basePalette,
      primary: {
        main: config.primary,
        contrastText: config.contrastText,
        dark: config.primaryDark,
        light: config.primaryLight,
      },
      secondary: {
        main: config.secondary,
        contrastText: config.secondaryContrastText,
        dark: config.secondaryDark,
        light: config.secondaryLight,
      },
      error: {
        main: config.error,
        contrastText: config.errorContrastText,
        dark: config.errorDark,
        light: config.errorLight,
      },
      background: {
        default: config.background,
        paper: config.paper,
      },
      link: config.link,
      divider: config.divider,
      navigation: {
        background: config.navigationBackground,
        indicator: config.navigationIndicator,
        color: config.navigationColor,
        selectedColor: config.navigationSelectedColor,
        navItem: {
          hoverBackground: config.navigationHoverBackground,
        },
        submenu: {
          background: config.navigationSubmenuBackground,
        },
      },
    },
  });
}

function createPageTheme(mode: 'light' | 'dark') {
  const defaultPageThemeOptionsLight = {
    colors: [themeColors.light.header, themeColors.light.header],
    shape: shapes.none,
    options: {
      fontColor: themeColors.light.text,
    },
  };

  const defaultPageThemeOptionsDark = {
    colors: [themeColors.dark.header, themeColors.dark.header],
    shape: shapes.none,
    options: {
      fontColor: themeColors.dark.text,
    },
  };

  const defaultPageThemeOptions =
    mode === 'light'
      ? defaultPageThemeOptionsLight
      : defaultPageThemeOptionsDark;

  return {
    home: genPageTheme(defaultPageThemeOptions),
    documentation: genPageTheme(defaultPageThemeOptions),
    tool: genPageTheme(defaultPageThemeOptions),
    service: genPageTheme(defaultPageThemeOptions),
    website: genPageTheme(defaultPageThemeOptions),
    library: genPageTheme(defaultPageThemeOptions),
    other: genPageTheme(defaultPageThemeOptions),
    app: genPageTheme(defaultPageThemeOptions),
    apis: genPageTheme(defaultPageThemeOptions),
  };
}

function createComponentOverrides(mode: 'light' | 'dark') {
  const config = themeColors[mode];

  return {
    BackstageHeader: {
      styleOverrides: {
        header: () => ({
          width: 'auto',
          boxShadow: 'none',
          borderBottom: `1px solid ${config.divider}`,
          backgroundColor: config.header,
          backgroundImage: 'none',
          color: mode === 'light' ? config.primary : config.navigationColor,
        }),
      },
    },
    BackstagePage: {
      styleOverrides: {
        root: {
          backgroundColor: config.background,
        },
      },
    },
    BackstageSidebar: {
      styleOverrides: {
        drawer: {
          borderRight: `1px solid ${config.divider}`,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: config.divider,
          color: config.navigationColor,
        },
        colorSecondary:
          mode === 'light'
            ? {
                backgroundColor: tokens.colors.primary,
                color: tokens.colors.white,
              }
            : {},
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: config.divider,
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: mode === 'light' ? tokens.colors.special.textLight : '#4B5563',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          color: mode === 'light' ? '#6B7280' : '#E5E7EB',
          '&.Mui-selected': {
            backgroundColor: `${
              mode === 'light' ? '#EEF1F7' : '#4B5563'
            } !important`,
            '&:hover': {
              backgroundColor: `${
                mode === 'light' ? '#F9FAFB' : '#374151'
              } !important`,
            },
          },
        },
        button: {
          '&:hover': {
            backgroundColor: `${
              mode === 'light' ? '#F9FAFB' : '#374151'
            } !important`,
          },
        },
      },
    },
    CatalogReactUserListPicker:
      mode === 'light'
        ? {
            styleOverrides: {
              root: {
                backgroundColor: '#c4c9e694',
              },
            },
          }
        : {},
    MuiAvatar: {
      styleOverrides: {
        colorDefault: {
          backgroundColor: mode === 'light' ? '#c4c9e694' : '#4B5563',
          color: mode === 'light' ? config.primary : '#E5E7EB',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: `${
              mode === 'light' ? 'rgb(249, 250, 251)' : '#9397bf0f'
            } !important`,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        containedPrimary:
          mode === 'dark'
            ? {
                // prototype: bg-primary-800 dark:bg-primary-100 → white bg + navy text in dark
                backgroundColor: tokens.colors.white,
                color: tokens.colors.primary,
                '&:hover': {
                  backgroundColor: tokens.colors.gray.light,
                },
              }
            : {},
      },
    },
  };
}

export const ciandtSpikeTheme = createUnifiedTheme({
  ...createPalette('light'),
  defaultPageTheme: 'home',
  pageTheme: createPageTheme('light'),
  components: createComponentOverrides('light'),
});

export const ciandtSpikeDarkTheme = createUnifiedTheme({
  ...createPalette('dark'),
  defaultPageTheme: 'home',
  pageTheme: createPageTheme('dark'),
  components: createComponentOverrides('dark'),
});
